import { Component, OnInit } from '@angular/core';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { BehaviorSubject, Observable, Subscriptions, combineLatest, debounceTime, map, op, shareReplay, tap } from '@lib/rxjs';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { generateChartLines } from '../chart-transforms';
import { fragQuery } from '../../../../../shared-models/frag-query';
import { math } from '@lib/math';
import { chart } from '@lib/angular/chart';
import { ActivatedRoute, Router } from '@angular/router';
import { combineInput } from '../analysis/analysis.component';
import { DXXData } from '@models';
import { snapshotManager } from '@app/utilities/snapshot/snapshot-manager';
import { asFiniteNumber, isValidNumber } from '@lib/number';

@Component({
    selector: 'log-details',
    templateUrl: './log-details.component.html',
    styleUrls: ['./log-details.component.scss']
})
export class LogDetailsComponent {
    constructor(
        private dialog: DialogService,
        private fragQuery: FragQueryService,
        private router: Router,
        private readonly route: ActivatedRoute,
    ) { }

    private subscriptions: Subscriptions = new Subscriptions();
    public selectedDevice: string | undefined;
    public dataArr: math.Vec2Like[];
    public percentArr: math.Vec2Like[];
    public dxxArr: DXXData[];
    public intervalsArray: any[] = null;
    public readonly devices$ = this.fragQuery.devices$;
    public readonly device$ = this.fragQuery.device$;
    public readonly quantityTimeline$ = this.fragQuery.timelineQuantity$;
    public isOffSpec: boolean = false

    ngOnInit(){
        this.subscriptions.add(this.intervals$.subscribe());
        this._handleFragment()
    }

    private _handleFragment(): void {
        const fragment = JSON.parse(this.route.snapshot.fragment);

        if (fragment?.device) {
            this.subscriptions.set('device',this.fragQuery.devices$.subscribe(devices => {
                const match = devices.find(device => device.id === fragment.device);
                if (match) {
                    this.fragQuery.deviceUuid$.next(match.uuid);
                }
            }))
        }

        if (fragment?.site) {
            const siteIdNum = asFiniteNumber(fragment.site);
            if (isValidNumber(siteIdNum)) {
                this.fragQuery.siteId$.next(siteIdNum);
            }
        }
    }

    public readonly chartLines$: Observable<chart.line.Series[]> = combineLatest([
        this.fragQuery.newPercents$,
        this.fragQuery.timelineSieveSizesAtPercents$,
        this.fragQuery.addOnTimelineSieveSizesAtPercents$
    ])
        .pipe(
            op.debounceTime(0),
            op.tap(([percents, input, addOn]) => combineInput(input, addOn, this.fragQuery.addDataAt.value)),
            op.map(([percents, input]) => {
                this.psddata$.next(input?.intervals);
                return generateChartLines(percents, input)
            }),
            op.shareReplay(1)
        );

    public readonly intervals$: Observable<fragQuery.timeline.sieveSizesAtPercents.Interval[]> = combineLatest([
        this.fragQuery.newPercents$,
        this.fragQuery.timelineSieveSizesAtPercents$,
        this.fragQuery.addOnTimelineSieveSizesAtPercents$
    ]).pipe(
        op.debounceTime(0),
        op.tap(([percents, input, addOn]) => {
            combineInput(input, addOn, this.fragQuery.addDataAt.value)
            if (input?.intervals) {
                snapshotManager.setIntervals(input.intervals);
            }
        }),
        op.tap(input => {
            if (input[1] && input[1].intervals) {
                this.fragQuery.totalIntervalLength.next((input[1].end.valueOf() - input[1].begin.valueOf()))
                this.fragQuery.intervalsLength.next(input[1].intervals.length)
            }
        }),
        op.map(([percents, input]) => {
            return input ? input.intervals : undefined
        }),
        op.shareReplay(1)
    );

    public index$: Observable<number> = snapshotManager.currentIndex$
    public readonly newPercents$ = this.fragQuery.newPercents$;
    public readonly psddata$ = new BehaviorSubject<fragQuery.timeline.sieveSizesAtPercents.Interval[]>(null)
    public readonly hasTargetSizes$ = combineLatest([
        this.psddata$,
        this.index$,
        this.device$,
    ]).pipe(
        op.map(([intervals, index, device]) => {
            const intervalTargetSizes = intervals?.[index]?.targetSizes;
            if (intervalTargetSizes !== undefined)
                return !!intervalTargetSizes?.length;

            return !!(
                device?.preferCustomSizes
                && device.targetSizes
                && device.targetSizes !== '[]'
            );
        }),
        op.shareReplay(1)
    );

    public setTargetIntervalSetting() {
        this.dialog.openTargetIntervalSetting()
    }

    onSelectDevice(event: any): void {
        this.selectedDevice = event.target.value;
    }

    switchPage() {
        this.router.navigateByUrl(`/analytics`);
    }

    openReportResults() {
        this.dialog.input({ label: 'Comments', title: 'Report Results' })
    }

    onDataArrChange(dataArr: math.Vec2Like[]) {
        this.dataArr = dataArr;
    }

    public setOffSpec(isOffSpec: boolean) {
        this.isOffSpec = isOffSpec
    }

    onPercentArrChange(percentArr: math.Vec2Like[]) {
        this.percentArr = percentArr;
    }

    onDxxArrChange(dxxArr: DXXData[]) {
        this.dxxArr = dxxArr
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
